import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import AskDaiChat from './AskDaiChat';
import { askDaiHref } from './navigation';

export default function AskDai() {
  const location = useLocation();
  const navigate = useNavigate();
  const open = new URLSearchParams(location.search).get('chat') === 'ask-dai';
  const buttonRef = useRef(null);
  const returnFocusRef = useRef(null);
  const openingRef = useRef(false);
  const closingRef = useRef(false);
  const normalisedEntryRef = useRef(null);

  useLayoutEffect(() => {
    openingRef.current = false;
    closingRef.current = false;
    if (!returnFocusRef.current?.isConnected) returnFocusRef.current = buttonRef.current;
  }, [location.key]);

  useEffect(() => {
    if (!open || location.state?.askDaiEntry || normalisedEntryRef.current === location.key) return;
    normalisedEntryRef.current = location.key;
    // A direct link has no underlying page entry. Keep the router's history metadata
    // when replacing this URL, then let the router push the panel entry above it.
    window.history.replaceState(window.history.state, '', askDaiHref(location, false));
    navigate(askDaiHref(location), { state: { ...location.state, askDaiEntry: true } });
  }, [location, navigate, open]);

  const openChat = useCallback((invoker) => {
    if (open || openingRef.current) return;
    openingRef.current = true;
    returnFocusRef.current = invoker?.isConnected ? invoker : buttonRef.current;
    navigate(askDaiHref(location), { state: { ...location.state, askDaiEntry: true } });
  }, [location, navigate, open]);

  useEffect(() => {
    const openFromMenu = (event) => openChat(event.detail?.invoker);
    window.addEventListener('inclusivecore:ask-dai-open', openFromMenu);
    return () => window.removeEventListener('inclusivecore:ask-dai-open', openFromMenu);
  }, [openChat]);

  const closeChat = () => {
    if (!open || closingRef.current) return;
    closingRef.current = true;
    if (location.state?.askDaiEntry) navigate(-1);
    else navigate(askDaiHref(location, false), { replace: true, state: location.state });
  };

  return (
    <>
      <div className="fixed bottom-4 right-4 z-40">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => openChat(buttonRef.current)}
          className="ask-dai-launcher flex min-h-11 items-center gap-2 rounded-full bg-teal-700 px-4 py-3 text-sm font-semibold text-white shadow-lg hover:bg-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FFB800] focus-visible:outline-offset-2"
          aria-label="Ask Dai — get disability inclusion guidance and support"
        >
          <Sparkles className="w-4 h-4 shrink-0" aria-hidden="true" />
          Ask Dai
        </button>
      </div>

      <AskDaiChat open={open} onClose={closeChat} returnFocusRef={returnFocusRef} />
    </>
  );
}
